import type { HealthStatus, OverallStatus } from "../types/api";

/**
 * Centralized mapping of API status enums to Tailwind class strings and Korean labels.
 *
 * Why a central map:
 *   - Status appears in 4+ places (banner, badge, site card border, bridge row).
 *     Inlining classes leads to drift; the badge ends up green while the banner
 *     stays gray.
 *   - Adding `DEGRADED_PERFORMANCE` later only needs an edit here.
 *
 * Accessibility note: callers must always render the label too — color alone
 * is not sufficient (status.claude.com follows the same rule).
 */

interface ToneClasses {
  /** Solid swatch background (small dots, indicator strips). */
  solid: string;
  /** Tinted background for banners/cards. */
  bg: string;
  /** Foreground text color matching `bg`. */
  fg: string;
  /** Subtle border color matching the tone. */
  border: string;
}

export interface OverallStatusStyle extends ToneClasses {
  /** Korean headline label, e.g. "정상 동작". */
  label: string;
  /** Short subheading shown under the banner headline. */
  description: string;
}

export interface HealthStatusStyle extends ToneClasses {
  label: string;
}

// ─── Overall status (banner / site card) ──────────────────────────────────────

export const OVERALL_STATUS_STYLES: Record<OverallStatus, OverallStatusStyle> = {
  OPERATIONAL: {
    label: "정상 동작",
    description: "모든 브릿지가 정상적으로 데이터를 전송하고 있습니다.",
    solid: "bg-status-operational dark:bg-status-operational-dark",
    bg: "bg-status-operational-bg dark:bg-status-operational-darkBg",
    fg: "text-status-operational-fg dark:text-status-operational-darkFg",
    border:
      "border-status-operational/30 dark:border-status-operational-dark/30",
  },
  DEGRADED_PERFORMANCE: {
    label: "성능 저하",
    description: "일부 사이트의 데이터 전송 지연이 감지되었습니다.",
    solid: "bg-status-degraded dark:bg-status-degraded-dark",
    bg: "bg-status-degraded-bg dark:bg-status-degraded-darkBg",
    fg: "text-status-degraded-fg dark:text-status-degraded-darkFg",
    border: "border-status-degraded/30 dark:border-status-degraded-dark/30",
  },
  PARTIAL_OUTAGE: {
    label: "부분 장애",
    description: "일부 브릿지에서 데이터 전송이 중단되었습니다.",
    solid: "bg-status-partialOutage dark:bg-status-partialOutage-dark",
    bg: "bg-status-partialOutage-bg dark:bg-status-partialOutage-darkBg",
    fg: "text-status-partialOutage-fg dark:text-status-partialOutage-darkFg",
    border:
      "border-status-partialOutage/30 dark:border-status-partialOutage-dark/30",
  },
  MAJOR_OUTAGE: {
    label: "주요 장애",
    description: "다수의 브릿지에서 데이터 전송이 중단되었습니다.",
    solid: "bg-status-majorOutage dark:bg-status-majorOutage-dark",
    bg: "bg-status-majorOutage-bg dark:bg-status-majorOutage-darkBg",
    fg: "text-status-majorOutage-fg dark:text-status-majorOutage-darkFg",
    border:
      "border-status-majorOutage/30 dark:border-status-majorOutage-dark/30",
  },
  UNKNOWN: {
    label: "확인 중",
    description: "데이터를 수집하고 있습니다. 잠시 후 다시 확인해주세요.",
    solid: "bg-status-unknown dark:bg-status-unknown-dark",
    bg: "bg-status-unknown-bg dark:bg-status-unknown-darkBg",
    fg: "text-status-unknown-fg dark:text-status-unknown-darkFg",
    border: "border-status-unknown/30 dark:border-status-unknown-dark/30",
  },
};

// ─── Health (per-bridge) ──────────────────────────────────────────────────────

export const HEALTH_STATUS_STYLES: Record<HealthStatus, HealthStatusStyle> = {
  UP: {
    label: "정상",
    solid: "bg-status-operational dark:bg-status-operational-dark",
    bg: "bg-status-operational-bg dark:bg-status-operational-darkBg",
    fg: "text-status-operational-fg dark:text-status-operational-darkFg",
    border:
      "border-status-operational/30 dark:border-status-operational-dark/30",
  },
  DOWN: {
    label: "데이터 정체",
    solid: "bg-status-majorOutage dark:bg-status-majorOutage-dark",
    bg: "bg-status-majorOutage-bg dark:bg-status-majorOutage-darkBg",
    fg: "text-status-majorOutage-fg dark:text-status-majorOutage-darkFg",
    border:
      "border-status-majorOutage/30 dark:border-status-majorOutage-dark/30",
  },
  STALE: {
    // 60일 이상 데이터 없음 — 휴면 추정. 회색 라벨로 분리.
    label: "미운영",
    solid: "bg-status-stale dark:bg-status-stale-dark",
    bg: "bg-status-stale-bg dark:bg-status-stale-darkBg",
    fg: "text-status-stale-fg dark:text-status-stale-darkFg",
    border: "border-status-stale/30 dark:border-status-stale-dark/30",
  },
  RETIRED: {
    // 운영자가 명시적으로 종료 마킹 (pkl_st='02'). 더 옅은 회색.
    label: "운영 종료",
    solid: "bg-status-retired dark:bg-status-retired-dark",
    bg: "bg-status-retired-bg dark:bg-status-retired-darkBg",
    fg: "text-status-retired-fg dark:text-status-retired-darkFg",
    border: "border-status-retired/30 dark:border-status-retired-dark/30",
  },
  UNKNOWN: {
    label: "확인 중",
    solid: "bg-status-unknown dark:bg-status-unknown-dark",
    bg: "bg-status-unknown-bg dark:bg-status-unknown-darkBg",
    fg: "text-status-unknown-fg dark:text-status-unknown-darkFg",
    border: "border-status-unknown/30 dark:border-status-unknown-dark/30",
  },
};

/** Defensive lookup that falls back to UNKNOWN if the server adds a new enum value. */
export function overallStyle(status: OverallStatus | string): OverallStatusStyle {
  return (
    OVERALL_STATUS_STYLES[status as OverallStatus] ??
    OVERALL_STATUS_STYLES.UNKNOWN
  );
}

export function healthStyle(status: HealthStatus | string): HealthStatusStyle {
  return (
    HEALTH_STATUS_STYLES[status as HealthStatus] ?? HEALTH_STATUS_STYLES.UNKNOWN
  );
}
